import React from "react";
import { Link } from "react-router-dom";
import {
    Sparkles,
    Users,
    Heart,
    Eye,
    MessageCircle,
    Hand,
    MapPin,
    ArrowRight,
    Star,
} from "lucide-react";
import { BRAND, FEATURED_QUOTES, TESTIMONIALS, EVENTS } from "../data/content";
import PolaroidCard from "../components/scrapbook/PolaroidCard";
import StickyNote from "../components/scrapbook/StickyNote";
import MaskingTape from "../components/scrapbook/MaskingTape";
import { PrimaryButton } from "../components/scrapbook/PrimaryButton";
import { useReveal } from "../hooks/useReveal";

const HeroBadge = () => (
    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border-2 border-ink/85 rotate-[-2deg] scrap-shadow">
        <Sparkles size={14} strokeWidth={2.5} className="text-marker-red" />
        <span className="font-sans font-bold text-xs tracking-[0.18em] uppercase text-ink">
            Real reviews · Real rooms
        </span>
    </div>
);

const Hero = () => {
    return (
        <section
            data-testid="hero-section"
            className="relative pt-32 sm:pt-36 pb-20 px-4 sm:px-6 overflow-hidden paper-noise"
        >
            {/* decorative scrapbook elements */}
            <div className="absolute top-32 right-8 sm:right-20 hidden md:block">
                <div className="relative bg-tape rotate-[8deg] px-5 py-3 scrap-shadow">
                    <span className="font-handwriting text-xl text-ink">
                        no more
                        <br />
                        <span className="text-marker-red font-bold">
                            awkward small talk
                        </span>
                    </span>
                </div>
            </div>
            <div className="absolute bottom-10 left-4 sm:left-16 hidden lg:block">
                <div className="relative bg-white rotate-[-6deg] p-3 scrap-shadow border border-neutral-200">
                    <MaskingTape
                        color="green"
                        className="-top-2 left-1/2 -translate-x-1/2 w-16 h-5"
                    />
                    <p className="font-handwriting text-lg text-ink-light px-2">
                        ★★★★★
                    </p>
                    <p className="font-marker text-2xl text-marker-red leading-tight px-2">
                        100% un-paid
                        <br />
                        un-coerced
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto text-center relative">
                <div className="flex justify-center mb-6">
                    <HeroBadge />
                </div>

                <h1
                    data-testid="hero-headline"
                    className="font-marker text-[3.4rem] sm:text-7xl md:text-8xl lg:text-[8.5rem] leading-[0.95] tracking-tight text-ink"
                >
                    Stop{" "}
                    <span className="relative inline-block text-marker-red">
                        Blind
                        <svg
                            className="absolute -bottom-1 left-0 w-full"
                            viewBox="0 0 200 12"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M2,8 Q50,2 100,6 T198,8"
                                stroke="#E05236"
                                strokeWidth="4"
                                fill="none"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                    <br />
                    Networking.
                </h1>

                <p className="font-editorial italic text-xl sm:text-2xl md:text-3xl text-ink-light mt-8 max-w-2xl mx-auto leading-snug">
                    See who&apos;s in the room{" "}
                    <span className="marker-highlight font-semibold not-italic">
                        before
                    </span>{" "}
                    you walk in. Real reviews from real attendees at Toronto&apos;s
                    best events.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <PrimaryButton
                        href={BRAND.parentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        testId="hero-cta-button"
                    >
                        {BRAND.cta}
                    </PrimaryButton>
                    <a
                        href="#reviews"
                        data-testid="hero-secondary-link"
                        className="font-handwriting text-xl text-ink-light hover:text-marker-red transition-colors underline decoration-2 decoration-ink/20 underline-offset-4"
                    >
                        ↓ read what folks are saying
                    </a>
                </div>

                {/* trust strip */}
                <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-ink-light">
                    <div className="flex items-center gap-2">
                        <div className="flex">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    size={16}
                                    className="text-marker-red fill-marker-red"
                                />
                            ))}
                        </div>
                        <span className="font-sans font-semibold text-sm">
                            Loved by event-goers
                        </span>
                    </div>
                    <span className="hidden sm:block text-ink/30">·</span>
                    <span className="font-handwriting text-lg">
                        Toronto · Tech · Health · Community
                    </span>
                </div>
            </div>
        </section>
    );
};

const FeaturedQuote = ({ data, icon: Icon, label, color, side = "left" }) => {
    const [ref, visible] = useReveal();
    return (
        <div
            ref={ref}
            className={`reveal-up ${visible ? "is-visible" : ""} grid md:grid-cols-12 gap-8 items-center`}
        >
            <div
                className={`md:col-span-5 ${side === "right" ? "md:order-2" : ""}`}
            >
                <div className="flex items-center gap-3 mb-4">
                    <span
                        className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                        style={{ backgroundColor: color, color: "#fff" }}
                    >
                        <Icon size={22} strokeWidth={2} />
                    </span>
                    <span className="font-handwriting text-2xl text-ink-light">
                        {label}
                    </span>
                </div>
                <h3 className="font-editorial text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-ink">
                    {side === "left"
                        ? "The fix for that pre-event pit in your stomach."
                        : "Even the people you almost missed."}
                </h3>
                <p className="font-sans text-base sm:text-lg text-ink-light mt-5 max-w-md leading-relaxed">
                    {side === "left"
                        ? "Walking into a room of strangers is a tax on your nervous system. PopIn refunds it."
                        : "Most networking apps brag about who showed up. PopIn helps you find the ones you didn't get to — after the fact."}
                </p>
            </div>

            <div
                className={`md:col-span-7 ${side === "right" ? "md:order-1" : ""}`}
            >
                <div
                    data-testid={`featured-quote-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="relative bg-white p-10 sm:p-14 border border-neutral-200 scrap-shadow rotate-[-1deg] hover:rotate-0 transition-transform duration-500"
                >
                    <MaskingTape
                        color={side === "left" ? "yellow" : "blue"}
                        className="-top-4 left-10 w-32 h-8"
                        rotate="-rotate-6"
                    />
                    <MaskingTape
                        color={side === "left" ? "green" : "yellow"}
                        className="-top-4 right-12 w-24 h-7"
                        rotate="rotate-3"
                    />

                    <span
                        className="absolute -top-6 -right-4 stamp text-sm"
                        style={{ color }}
                    >
                        {data.tag}
                    </span>

                    <p className="font-editorial italic text-3xl sm:text-4xl md:text-[2.6rem] leading-[1.2] text-ink relative">
                        <span
                            className="absolute -left-2 -top-4 font-marker text-7xl opacity-15"
                            style={{ color }}
                            aria-hidden="true"
                        >
                            “
                        </span>
                        {data.text}
                    </p>
                    <div className="mt-8 flex items-center justify-between">
                        <p className="font-handwriting text-xl text-ink-light">
                            — {data.author}
                        </p>
                        <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    size={16}
                                    className="fill-marker-red text-marker-red"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AnxietySolver = () => (
    <section
        id="anxiety"
        data-testid="anxiety-section"
        className="relative py-24 sm:py-32 px-4 sm:px-6"
    >
        <div className="max-w-7xl mx-auto">
            <FeaturedQuote
                data={FEATURED_QUOTES.anxiety}
                icon={Heart}
                label="The Anxiety Solver"
                color="#E05236"
                side="left"
            />
        </div>
    </section>
);

const Efficiency = () => (
    <section
        id="efficiency"
        data-testid="efficiency-section"
        className="relative py-24 sm:py-32 px-4 sm:px-6 bg-paper-deep/40 border-y border-dashed border-ink/15"
    >
        <div className="max-w-7xl mx-auto">
            <FeaturedQuote
                data={FEATURED_QUOTES.efficiency}
                icon={Eye}
                label="The Room Behind the Room"
                color="#2B5F7E"
                side="right"
            />
        </div>
    </section>
);

const ReviewsWall = () => {
    const [ref, visible] = useReveal();
    return (
        <section
            id="reviews"
            data-testid="reviews-wall-section"
            className="relative py-24 sm:py-32 px-4 sm:px-6"
        >
            <div className="max-w-7xl mx-auto">
                <div
                    ref={ref}
                    className={`reveal-up ${visible ? "is-visible" : ""} text-center mb-16 sm:mb-20`}
                >
                    <span className="font-handwriting text-2xl text-marker-red rotate-[-2deg] inline-block">
                        the scrapbook
                    </span>
                    <h2 className="font-editorial text-5xl sm:text-6xl text-ink mt-2">
                        Receipts.{" "}
                        <span className="italic marker-highlight">
                            Not hype.
                        </span>
                    </h2>
                    <p className="font-sans text-lg text-ink-light max-w-xl mx-auto mt-6">
                        Every quote on this wall came from a real PopIn user.
                        Pin them up like postcards from a really good party.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    {TESTIMONIALS.map((t, i) => (
                        <PolaroidCard
                            key={t.id}
                            quote={t.text}
                            author={t.author}
                            tape={t.tape}
                            rotation={t.rotation}
                            delay={i * 80}
                            testId={`testimonial-card-${t.id}`}
                        />
                    ))}

                    {/* "your voice next" placeholder card */}
                    <a
                        href={BRAND.parentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="testimonial-card-yours"
                        className="relative bg-white/60 p-7 pb-12 border-2 border-dashed border-ink/30 flex flex-col items-center justify-center min-h-[260px] rotate-[1deg] hover:rotate-0 hover:bg-white transition-all duration-500 group"
                    >
                        <span className="font-handwriting text-2xl text-ink-light text-center">
                            your name
                            <br />
                            could be here ↓
                        </span>
                        <span className="mt-4 font-marker text-3xl text-marker-red group-hover:scale-110 transition-transform">
                            Try PopIn
                        </span>
                    </a>
                </div>
            </div>
        </section>
    );
};

const EveryVoice = () => {
    const [ref, visible] = useReveal();
    return (
        <section
            id="community"
            data-testid="every-voice-section"
            className="relative py-24 sm:py-32 px-4 sm:px-6 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div
                    ref={ref}
                    className={`reveal-up ${visible ? "is-visible" : ""}`}
                >
                    <span className="font-handwriting text-2xl text-marker-red rotate-[-1deg] inline-flex items-center gap-2">
                        <MessageCircle size={20} /> every voice
                    </span>
                    <h2 className="font-editorial text-5xl sm:text-6xl md:text-7xl text-ink mt-3 leading-[1.05]">
                        Every voice
                        <br />
                        <span className="italic">gets heard.</span>
                    </h2>
                    <p className="font-sans text-lg text-ink-light mt-6 max-w-md leading-relaxed">
                        The introvert in the corner. The first-time founder.
                        The plus-one nobody asked. On PopIn, the loudest
                        person in the room doesn&apos;t get to write the story
                        of who was in it.
                    </p>
                    <div className="mt-8 flex gap-3 flex-wrap">
                        <span className="px-4 py-2 bg-tape font-handwriting text-lg rotate-[-2deg]">
                            introverts welcome
                        </span>
                        <span className="px-4 py-2 bg-highlight font-handwriting text-lg rotate-[1deg]">
                            first-timers welcome
                        </span>
                        <span className="px-4 py-2 bg-[#F7D5D2] font-handwriting text-lg rotate-[-1deg]">
                            everyone welcome
                        </span>
                    </div>
                </div>

                <div className="relative h-[420px]">
                    <StickyNote
                        color="yellow"
                        rotation="-rotate-3"
                        className="absolute top-0 left-4 w-72 z-10"
                        testId="sticky-note-1"
                    >
                        <Hand
                            className="text-marker-red mb-3"
                            size={28}
                            strokeWidth={1.8}
                        />
                        <p className="font-handwriting text-2xl text-ink leading-snug">
                            &ldquo;I went up to him because the app said he
                            wanted to chat about the same thing I do.&rdquo;
                        </p>
                    </StickyNote>
                    <StickyNote
                        color="green"
                        rotation="rotate-2"
                        className="absolute top-32 right-0 w-72 z-20"
                        testId="sticky-note-2"
                    >
                        <Users
                            className="text-ink mb-3"
                            size={28}
                            strokeWidth={1.8}
                        />
                        <p className="font-handwriting text-2xl text-ink leading-snug">
                            &ldquo;PopIn is how shy people make the first
                            move without feeling like they did.&rdquo;
                        </p>
                    </StickyNote>
                    <StickyNote
                        color="pink"
                        rotation="-rotate-1"
                        className="absolute bottom-0 left-12 w-72 z-30"
                        testId="sticky-note-3"
                    >
                        <Heart
                            className="text-marker-red mb-3"
                            size={28}
                            strokeWidth={1.8}
                        />
                        <p className="font-handwriting text-2xl text-ink leading-snug">
                            &ldquo;The badge said hello before I had to.&rdquo;
                        </p>
                    </StickyNote>
                </div>
            </div>
        </section>
    );
};

const CommunityBonding = () => {
    const [ref, visible] = useReveal();
    return (
        <section
            data-testid="community-bonding-section"
            className="relative py-24 sm:py-32 px-4 sm:px-6 bg-ink text-paper overflow-hidden paper-noise"
        >
            <div className="max-w-6xl mx-auto text-center">
                <div
                    ref={ref}
                    className={`reveal-up ${visible ? "is-visible" : ""}`}
                >
                    <span className="font-handwriting text-2xl text-tape inline-block rotate-[-2deg]">
                        the secret sauce
                    </span>
                    <h2 className="font-marker text-6xl sm:text-7xl md:text-8xl text-paper mt-3 leading-[0.95]">
                        Community
                        <br />
                        <span className="text-marker-red">bonding,</span>{" "}
                        <span className="italic font-editorial font-normal">
                            not bidding.
                        </span>
                    </h2>
                    <p className="font-editorial italic text-xl sm:text-2xl text-paper/75 mt-8 max-w-2xl mx-auto leading-snug">
                        PopIn rooms are warmer because everyone walks in
                        already half-knowing each other. That&apos;s not magic.
                        That&apos;s a really good guest list, served in your
                        pocket.
                    </p>

                    <div className="mt-14 grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
                        {[
                            {
                                num: "01",
                                title: "Skim the room",
                                body: "Open the app and see who&apos;s actually there — bios, interests, the whole vibe.",
                            },
                            {
                                num: "02",
                                title: "Spot your people",
                                body: "Filter by what matters. Founders, PMs, clinicians, whoever you came for.",
                            },
                            {
                                num: "03",
                                title: "Pop in IRL",
                                body: "Walk over with a reason to say hi. No awkward openers required.",
                            },
                        ].map((step) => (
                            <div
                                key={step.num}
                                data-testid={`bonding-step-${step.num}`}
                                className="text-left bg-paper/5 border border-paper/10 p-6 hover:bg-paper/10 transition-colors"
                            >
                                <span className="font-marker text-5xl text-marker-red">
                                    {step.num}
                                </span>
                                <h3 className="font-editorial text-2xl text-paper mt-2">
                                    {step.title}
                                </h3>
                                <p
                                    className="font-sans text-sm text-paper/65 mt-2 leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: step.body,
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="mt-14">
                        <PrimaryButton
                            href={BRAND.parentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            testId="bonding-cta-button"
                            className="!bg-marker-red !border-marker-red !shadow-[6px_6px_0px_0px_rgba(252,235,167,1)] hover:!shadow-[3px_3px_0px_0px_rgba(252,235,167,1)]"
                        >
                            {BRAND.cta}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </section>
    );
};

const EventsGrid = () => {
    const [ref, visible] = useReveal();
    return (
        <section
            id="events"
            data-testid="events-section"
            className="relative py-24 sm:py-32 px-4 sm:px-6"
        >
            <div className="max-w-7xl mx-auto">
                <div
                    ref={ref}
                    className={`reveal-up ${visible ? "is-visible" : ""} flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14`}
                >
                    <div>
                        <span className="font-handwriting text-2xl text-marker-red rotate-[-1deg] inline-flex items-center gap-2">
                            <MapPin size={20} /> Toronto · 2026
                        </span>
                        <h2 className="font-editorial text-5xl sm:text-6xl text-ink mt-2 leading-tight">
                            Reviews,
                            <br className="sm:hidden" /> event by event.
                        </h2>
                    </div>
                    <p className="font-sans text-base text-ink-light max-w-sm">
                        Pick an event, read what attendees actually said about
                        meeting each other inside it.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {EVENTS.map((event, i) => (
                        <Link
                            key={event.id}
                            to={`/reviews/${event.id}`}
                            data-testid={`event-card-${event.id}`}
                            className={`group relative bg-white p-7 pb-10 border border-neutral-200 scrap-shadow ${event.rotation} hover:rotate-0 hover:scale-[1.03] transition-all duration-500`}
                            style={{ transitionDelay: `${i * 60}ms` }}
                        >
                            <MaskingTape
                                color={event.tape}
                                className="-top-3 left-1/2 -translate-x-1/2 w-32 h-7"
                            />
                            <div
                                className="font-marker text-3xl leading-tight"
                                style={{ color: event.color }}
                            >
                                {event.shortName}
                            </div>
                            <div className="dashed-divider my-4" />
                            <p className="font-handwriting text-lg text-ink-light">
                                {event.city}
                            </p>
                            <p className="font-editorial italic text-lg text-ink mt-3 leading-snug">
                                {event.vibe}
                            </p>
                            <span className="absolute bottom-6 right-6 inline-flex items-center gap-1 font-sans font-bold text-sm text-ink group-hover:text-marker-red transition-colors">
                                read reviews
                                <ArrowRight
                                    size={16}
                                    strokeWidth={2.5}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

const FinalCTA = () => (
    <section
        data-testid="final-cta-section"
        className="relative py-24 sm:py-32 px-4 sm:px-6"
    >
        <div className="max-w-3xl mx-auto text-center relative">
            <MaskingTape
                color="yellow"
                className="-top-6 left-1/2 -translate-x-1/2 w-40 h-9 z-0"
            />
            <div className="relative bg-white border border-neutral-200 scrap-shadow p-12 sm:p-16">
                <h2 className="font-marker text-5xl sm:text-6xl md:text-7xl text-ink leading-[0.95]">
                    The next event
                    <br />
                    <span className="text-marker-red">doesn&apos;t have to</span>
                    <br />
                    be blind.
                </h2>
                <p className="font-editorial italic text-xl text-ink-light mt-6 max-w-md mx-auto">
                    Pop in once. You&apos;ll never walk into a room cold
                    again.
                </p>
                <div className="mt-10">
                    <PrimaryButton
                        href={BRAND.parentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        testId="final-cta-button"
                    >
                        {BRAND.cta}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    </section>
);

export default function Home() {
    return (
        <main data-testid="home-page">
            <Hero />
            <AnxietySolver />
            <Efficiency />
            <ReviewsWall />
            <EveryVoice />
            <CommunityBonding />
            <EventsGrid />
            <FinalCTA />
        </main>
    );
}
